import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { OrganizationsService } from 'src/app/modules/organizations/services/organizations.service';
import { MdrDashboardsService } from '../../services/mdr-dashboards.service';
import { MultiTenantWidgets } from '../../interfaces/mdr-dashboards-widgets.interface';
import { distinctUntilChanged } from 'rxjs/operators';
import { TimeframeData } from '../../../timeframe/interfaces/TimeframeData';
import { TimeframeService } from '../../../timeframe/services/timeframe.service';
import { OrganizationData } from '../../../organizations/interfaces/OrganizationData';
import { isEqual } from 'lodash-es';
import { Subscription } from 'rxjs';
import { IdentityService } from '../../../auth/services/identity.service';

@Component({
    selector: 'app-mdr-dashboards-org-summary',
    templateUrl: './mdr-dashboards-org-summary.component.html',
    styleUrls: ['./mdr-dashboards-org-summary.component.scss'],
    standalone: false,
})
export class MdrDashboardsOrgSummaryComponent implements OnInit, OnDestroy {
    public orgSubscription: Subscription;
    public timeframeSubscription: Subscription;
    public dataSource: MultiTenantWidgets;
    public companyLoadedSub: Subscription;
    public companyLoaded = false;
    public organization: OrganizationData;

    public constructor(
        public organizationService: OrganizationsService,
        public identityService: IdentityService,
        public mdrDashboardsService: MdrDashboardsService,
        public cdr: ChangeDetectorRef,
        public timeframeService: TimeframeService,
    ) {}

    ngOnInit() {
        this.companyLoadedSub = this.identityService.companyLoaded$.subscribe(() => {
            this.companyLoaded = true;
            this.cdr.detectChanges();
        });
        this.timeframeSubscription = this.timeframeService
            .selectedTimeframe$()
            .pipe(distinctUntilChanged(isEqual))
            .subscribe((timeframe: TimeframeData) => {
                this.updateDataSource(timeframe, this.organizationService.currentlySelectedOrganization());
            });

        this.orgSubscription = this.organizationService
            .selectedOrganization$()
            .pipe(distinctUntilChanged(isEqual))
            .subscribe((organization: OrganizationData) => {
                this.updateDataSource(this.timeframeService.currentlySelectedTimeframe(), organization);
                this.organization = organization;
                this.cdr.detectChanges();
            });
    }

    ngOnDestroy() {
        this.companyLoadedSub.unsubscribe();
        this.orgSubscription?.unsubscribe();
        this.timeframeSubscription?.unsubscribe();
        this.cdr.detach();
    }

    /**
     * Use this function to update each widget when one of the selectors (organization and timeframe) are changed
     * @param timeframe - retrieve data for the selected timeframe
     * @param organization - retrieve data for the selected organization
     */
    public updateDataSource(timeframe: TimeframeData | null, organization: OrganizationData | null) {
        this.dataSource = {
            indicatorOptions: [
                {
                    title: 'Managed companies',
                    value: 83,
                    data: this.mdrDashboardsService.getWidgetData(timeframe?.name, '/managed-companies', organization),
                    drilldown: {
                        show: true,
                        drilldownTo: '/companies/browse',
                        drilldownFilters: [],
                    },
                    id: 'managed_companies_indicator',
                },
                {
                    title: 'Monitored endpoints',
                    value: 462,
                    data: this.mdrDashboardsService.getWidgetData(timeframe?.name, '/monitored-endpoints', organization),
                    id: 'monitored_endpoints_indicator',
                },
            ],
            topInvestigationCategories: {
                id: 'top_investigations_table',
                data: this.mdrDashboardsService.getWidgetData(timeframe?.name, '/top-investigations', organization),
                columns: ['category', 'name', 'value'],
                columnsWidth: [
                    {
                        column: 'category',
                        width: '40%',
                    },
                    {
                        column: 'name',
                        width: '50%',
                    },
                    {
                        column: 'value',
                        width: '10%',
                    },
                ],
                no_rows: 10,
                title: 'Top investigations',
                no_columns: 3,
                drilldown: {
                    show: true,
                    hover_color: 'secondary',
                    drilldownTo: '/investigations/browse',
                    drilldownTitle: true,
                    drilldownFilters: [
                        {
                            item: {
                                apiName: 'name',
                                nativeName: 'detection_name',
                            },
                        },
                        { item: 'timeframe' },
                        { item: 'category' },
                    ],
                    drilldownTitleFilters: [{ item: 'timeframe' }],
                },
            },
            recentNotifications: {
                id: 'recent_notifications_table',
                data: this.mdrDashboardsService.getWidgetData(timeframe?.name, '/recent-notifications', organization),
                columns: ['processed_date', 'company_name', 'readable_category'],
                columnsWidth: [
                    {
                        column: 'processed_date',
                        width: '30%',
                    },
                    {
                        column: 'readable_category',
                        width: '40%',
                    },
                    {
                        column: 'company_name',
                        width: '40%',
                    },
                ],
                no_rows: 10,
                title: 'Recent notifications',
                no_columns: 3,
                drilldown: {
                    show: true,
                    hover_color: 'secondary',
                    drilldownTo: [
                        {
                            category: 'monthly_report',
                            url: '/reports/browse',
                        },
                        {
                            category: 'tipper_report',
                            url: '/reports/browse',
                        },
                        {
                            category: 'incident_report',
                            url: '/reports/browse',
                        },
                        {
                            category: 'investigation',
                            url: '/investigations/browse',
                        },
                        {
                            category: 'hunt',
                            url: '/threat-hunts/browse',
                        },
                        {
                            category: 'recommendation',
                            url: '/recommendations/browse',
                        },
                        {
                            category: 'miscellaneous',
                            url: '/',
                        },
                    ],
                    drilldownFilters: [
                        {
                            item: {
                                apiName: 'external_id',
                                nativeName: 'recommendation_external_id',
                            },
                        },
                        { item: 'filename' },
                        { item: 'id' },
                        {
                            item: {
                                apiName: 'case_number',
                                nativeName: 'investigation_case_number',
                            },
                        },
                        { item: 'cid' },
                        {
                            item: {
                                apiName: 'processed_date',
                                nativeName: 'uploaded_on',
                            },
                        },
                        {
                            item: {
                                apiName: 'category',
                                nativeName: 'type',
                            },
                        },
                        {
                            item: {
                                apiName: 'processed_date',
                                nativeName: 'created_at',
                            },
                        },
                        {
                            item: 'company_name',
                        },
                    ],
                    drilldownTitle: false,
                    drilldownTitleFilters: [],
                },
            },
            recentIncidents: {
                id: 'recent_incidents_table',
                data: this.mdrDashboardsService.getWidgetData(timeframe?.name, '/recent-incidents', organization),
                no_rows: 5,
                title: 'Recent incidents',
                columns: ['date', 'name', 'source', 'description'],
                columnsWidth: [
                    {
                        column: 'date',
                        width: '25%',
                    },
                    {
                        column: 'name',
                        width: '25%',
                    },
                    {
                        column: 'source',
                        width: '25%',
                    },
                    {
                        column: 'description',
                        width: '25%',
                    },
                ],
                drilldown: {
                    show: true,
                    hover_color: 'secondary',
                    drilldownTo: '/reports/browse',
                    drilldownTitle: true,
                    drilldownFilters: [
                        {
                            item: {
                                apiName: 'name',
                                nativeName: 'company_name',
                            },
                        },
                        { item: 'cid' },
                        { item: 'timeframe' },
                        {
                            item: {
                                apiName: 'description',
                                nativeName: 'type',
                                customValue: '5',
                            },
                        },
                    ],
                    drilldownTitleFilters: [
                        { item: 'timeframe' },
                        {
                            item: {
                                apiName: 'description',
                                nativeName: 'type',
                                customValue: '5',
                            },
                        },
                    ],
                },
            },
            topCustomersWithRecommendations: {
                id: 'top_customers_with_recommendations_table',
                data: this.mdrDashboardsService.getWidgetData(timeframe?.name, '/top-customers-recommendations', organization),
                no_rows: 10,
                title: 'Top customers with recommendations',
                columns: ['name', 'value'],
                columnsWidth: [
                    {
                        column: 'name',
                        width: '80%',
                    },
                    {
                        column: 'value',
                        width: '20%',
                    },
                ],
                drilldown: {
                    show: true,
                    hover_color: 'secondary',
                    drilldownTo: '/recommendations/browse',
                    drilldownTitle: true,
                    drilldownFilters: [
                        {
                            item: {
                                apiName: 'name',
                                nativeName: 'company_name',
                            },
                        },
                        { item: 'cid' },
                        { item: 'timeframe' },
                    ],
                    drilldownTitleFilters: [{ item: 'timeframe' }],
                },
            },
            topCustomersWithOpenTickets: {
                id: 'top_customers_with_openTickets_table',
                data: this.mdrDashboardsService.getWidgetData(timeframe?.name, '/top-customers-open-tickets', organization),
                no_rows: 10,
                columns: ['name', 'value'],
                columnsWidth: [
                    {
                        column: 'name',
                        width: '80%',
                    },
                    {
                        column: 'value',
                        width: '20%',
                    },
                ],
                title: 'Top customers with open tickets',
                drilldown: {
                    show: true,
                    hover_color: 'secondary',
                    drilldownTo: '/support-tickets/browse',
                    drilldownFilters: [
                        {
                            item: {
                                apiName: 'name',
                                nativeName: 'company_name',
                            },
                        },
                        { item: 'cid' },
                        {
                            item: {
                                apiName: 'status',
                                nativeName: 'status',
                                customValue: 'open',
                            },
                        },
                    ],
                    drilldownTitle: true,
                    drilldownTitleFilters: [
                        {
                            item: {
                                apiName: 'status',
                                nativeName: 'status',
                                customValue: 'open',
                            },
                        },
                    ],
                },
            },
            topCustomersWithInvestigations: {
                id: 'top_customers_with_investigations_table',
                data: this.mdrDashboardsService.getWidgetData(timeframe?.name, '/top-customers-investigations', organization),
                no_rows: 10,
                columns: ['name', 'value'],
                columnsWidth: [
                    {
                        column: 'name',
                        width: '80%',
                    },
                    {
                        column: 'value',
                        width: '20%',
                    },
                ],
                title: 'Top customers with investigations',
                drilldown: {
                    show: true,
                    hover_color: 'secondary',
                    drilldownTo: '/investigations/browse',
                    drilldownTitle: true,
                    drilldownFilters: [
                        {
                            item: {
                                apiName: 'name',
                                nativeName: 'company_name',
                            },
                        },
                        { item: 'timeframe' },
                        { item: 'cid' },
                    ],
                    drilldownTitleFilters: [{ item: 'timeframe' }],
                },
            },
            licenseStatus: {
                data: this.mdrDashboardsService.getWidgetData(timeframe?.name, '/license-status', organization),
                noDataLabels: ['Remaining', 'Used'],
                noDataSeries: [100, 0],
                colors: ['#00CCBD', '#FFB74D'],
                title: 'License Status',
                approximate: true,
            },
        };
    }
}
